import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Lock, X } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { api, errorMessage } from '../lib/api';
import { ASSET_STATUS_LABEL, dueDateInputToIso, formatDate } from '../lib/format';
import { invalidateInventory, useBuildings } from '../lib/hooks';
import { isOpenOn, placeLabel } from '../lib/locations';
import type { AssetListItem, CartSummary } from '../lib/types';
import { AssetSearch, AssigneePicker, assigneeToInput, type AssigneeValue } from './pickers';
import { useToast } from './toast';
import { Alert, Badge, Button, Field, Input, Modal, Select, Textarea } from './ui';

const homeOf = (cart: Pick<CartSummary, 'homeBuilding' | 'homeRoom'>) => placeLabel({ room: cart.homeRoom?.name, building: cart.homeBuilding.name });

/** Building and room selects that offer only open places (plus whatever is already chosen). */
export function PlaceSelects({
  buildingId,
  roomId,
  onChange,
  disabled,
}: {
  buildingId: string;
  roomId: string;
  onChange(buildingId: string, roomId: string): void;
  disabled?: boolean;
}) {
  const buildings = useBuildings();
  const options = (buildings.data ?? []).filter((building) => isOpenOn(building) || building.id === buildingId);
  const rooms = (options.find((building) => building.id === buildingId)?.rooms ?? []).filter((room) => isOpenOn(room) || room.id === roomId);
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <Field label="Building" required>
        <Select value={buildingId} onChange={(e) => onChange(e.target.value, '')} disabled={disabled} required>
          <option value="">Choose a building</option>
          {options.map((building) => (
            <option key={building.id} value={building.id}>
              {building.name}
              {building.kind === 'WAREHOUSE' ? ' (warehouse)' : ''}
            </option>
          ))}
        </Select>
      </Field>
      <Field label="Room">
        <Select value={roomId} onChange={(e) => onChange(buildingId, e.target.value)} disabled={disabled || !buildingId}>
          <option value="">No room</option>
          {rooms.map((room) => (
            <option key={room.id} value={room.id}>
              {room.name}
            </option>
          ))}
        </Select>
      </Field>
    </div>
  );
}

export function CartFormModal({
  open,
  onClose,
  cart,
  onSaved,
}: {
  open: boolean;
  onClose(): void;
  cart?: CartSummary | null;
  onSaved?(cart: CartSummary): void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ name: '', code: '', capacity: '', notes: '', homeBuildingId: '', homeRoomId: '' });
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setError('');
    setForm({
      name: cart?.name ?? '',
      code: cart?.code ?? '',
      capacity: cart?.capacity ? String(cart.capacity) : '',
      notes: cart?.notes ?? '',
      homeBuildingId: cart?.homeBuildingId ?? '',
      homeRoomId: cart?.homeRoomId ?? '',
    });
  }, [open, cart]);

  const mutation = useMutation({
    mutationFn: () => {
      const fields = { name: form.name, code: form.code, capacity: form.capacity, notes: form.notes };
      return cart
        ? api.patch<CartSummary>(`/carts/${cart.id}`, fields)
        : api.post<CartSummary>('/carts', { ...fields, homeBuildingId: form.homeBuildingId, homeRoomId: form.homeRoomId });
    },
    onSuccess: (saved) => {
      invalidateInventory(queryClient);
      toast.success(cart ? 'Cart saved' : `Added ${saved.name}`);
      onSaved?.(saved);
      onClose();
    },
    onError: (err) => setError(errorMessage(err)),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    mutation.mutate();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={cart ? `Edit ${cart.name}` : 'New cart'}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" form="cart-form" loading={mutation.isPending}>
            {cart ? 'Save' : 'Add cart'}
          </Button>
        </>
      }
    >
      <form id="cart-form" onSubmit={submit} className="space-y-4">
        {error && <Alert tone="red">{error}</Alert>}
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="sm:col-span-2">
            <Field label="Name" required>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required maxLength={120} autoFocus placeholder="Cart 3B" />
            </Field>
          </div>
          <Field label="Holds" hint="Devices">
            <Input type="number" min={1} max={200} value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} />
          </Field>
        </div>
        <Field label="Label or asset tag" hint="What’s printed on the cart, if anything">
          <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} maxLength={40} className="font-mono" />
        </Field>
        {cart ? (
          <p className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">
            Lives in <strong>{homeOf(cart)}</strong>. Use Move cart to change that, so every device on it moves too.
          </p>
        ) : (
          <>
            <PlaceSelects
              buildingId={form.homeBuildingId}
              roomId={form.homeRoomId}
              onChange={(homeBuildingId, homeRoomId) => setForm({ ...form, homeBuildingId, homeRoomId })}
            />
            <p className="-mt-2 text-xs text-slate-500">Devices on the cart take its building and room.</p>
          </>
        )}
        <Field label="Notes">
          <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} maxLength={2000} rows={2} />
        </Field>
      </form>
    </Modal>
  );
}

export function MoveCartDialog({ open, onClose, cart }: { open: boolean; onClose(): void; cart: CartSummary }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const buildings = useBuildings();
  const [place, setPlace] = useState({ buildingId: '', roomId: '' });
  const [note, setNote] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setPlace({ buildingId: cart.homeBuildingId, roomId: cart.homeRoomId ?? '' });
    setNote('');
    setError('');
  }, [open, cart]);

  const building = buildings.data?.find((b) => b.id === place.buildingId);
  const room = building?.rooms.find((r) => r.id === place.roomId);
  const destination = placeLabel({ room: room?.name, building: building?.name });
  const unchanged = place.buildingId === cart.homeBuildingId && (place.roomId || null) === cart.homeRoomId;
  const locked = !!cart.activeLease;

  const mutation = useMutation({
    mutationFn: () => api.post(`/carts/${cart.id}/move`, { buildingId: place.buildingId, roomId: place.roomId, note }),
    onSuccess: () => {
      invalidateInventory(queryClient);
      toast.success(`Moved ${cart.name} to ${destination}`);
      onClose();
    },
    onError: (err) => setError(errorMessage(err)),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Move ${cart.name}`}
      description={`Now in ${homeOf(cart)}`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={mutation.isPending} disabled={locked || unchanged || !place.buildingId} onClick={() => mutation.mutate()}>
            Move cart
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && <Alert tone="red">{error}</Alert>}
        {locked ? (
          <Alert tone="amber" icon={<Lock />} title="Home location locked">
            {cart.name} is out on lease to {cart.activeLease!.assigneeLabel}. Return it before moving it, so the cart and its devices can’t drift apart
            mid-rotation.
          </Alert>
        ) : (
          <p className="text-sm text-slate-600">
            {cart.deviceCount === 0
              ? 'There are no devices on this cart.'
              : `The ${cart.deviceCount === 1 ? 'device' : `${cart.deviceCount} devices`} on it move${cart.deviceCount === 1 ? 's' : ''} with it, all at once.`}
          </p>
        )}
        <PlaceSelects buildingId={place.buildingId} roomId={place.roomId} onChange={(buildingId, roomId) => setPlace({ buildingId, roomId })} disabled={locked} />
        {!locked && !unchanged && place.buildingId && (
          <p className="text-sm text-slate-600">
            {homeOf(cart)} → <strong>{destination}</strong>
          </p>
        )}
        <Field label="Note" hint="Optional, e.g. why it moved">
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} rows={2} disabled={locked} />
        </Field>
      </div>
    </Modal>
  );
}

export function LeaseCartDialog({ open, onClose, cart }: { open: boolean; onClose(): void; cart: CartSummary }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [assignee, setAssignee] = useState<AssigneeValue>({ type: 'PERSON', person: null });
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setAssignee({ type: 'PERSON', person: null });
    setDueDate('');
    setNotes('');
    setError('');
  }, [open]);

  const input = assigneeToInput(assignee);
  const mutation = useMutation({
    mutationFn: () => api.post(`/carts/${cart.id}/lease`, { assignee: input, dueAt: dueDateInputToIso(dueDate), notes }),
    onSuccess: () => {
      invalidateInventory(queryClient);
      toast.success(`Leased ${cart.name}`);
      onClose();
    },
    onError: (err) => setError(errorMessage(err)),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Lease ${cart.name}`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={mutation.isPending} disabled={!input} onClick={() => mutation.mutate()}>
            Lease cart
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && <Alert tone="red">{error}</Alert>}
        <Alert tone="blue" icon={<Lock />}>
          While it’s out, the cart’s home location ({homeOf(cart)}) is locked. The devices on it stay available to check out.
        </Alert>
        <Field as="div" label="Lease to" required>
          <AssigneePicker inline value={assignee} onChange={setAssignee} />
        </Field>
        <Field label="Due back" hint={dueDate ? `Due ${formatDate(dueDateInputToIso(dueDate))}` : 'No due date'}>
          <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="w-44" />
        </Field>
        <Field label="Notes">
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={2000} rows={2} />
        </Field>
      </div>
    </Modal>
  );
}

export function ReturnCartDialog({ open, onClose, cart }: { open: boolean; onClose(): void; cart: CartSummary }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  useEffect(() => {
    if (open) {
      setNotes('');
      setError('');
    }
  }, [open]);

  const mutation = useMutation({
    mutationFn: () => api.post(`/carts/${cart.id}/return`, { notes }),
    onSuccess: () => {
      invalidateInventory(queryClient);
      toast.success(`${cart.name} is back`);
      onClose();
    },
    onError: (err) => setError(errorMessage(err)),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="sm"
      title={`Return ${cart.name}`}
      description={cart.activeLease ? `From ${cart.activeLease.assigneeLabel}` : undefined}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={mutation.isPending} onClick={() => mutation.mutate()}>
            Return cart
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && <Alert tone="red">{error}</Alert>}
        <Field label="Notes" hint="E.g. anything missing or damaged">
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={2000} rows={3} autoFocus />
        </Field>
      </div>
    </Modal>
  );
}

export function AddCartDevicesDialog({ open, onClose, cart, onCartIds }: { open: boolean; onClose(): void; cart: CartSummary; onCartIds: string[] }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<AssetListItem[]>([]);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setSelected([]);
      setError('');
    }
  }, [open]);

  const problem = (asset: AssetListItem) =>
    asset.cart && asset.cart.id !== cart.id
      ? `On ${asset.cart.name}`
      : asset.status === 'RETIRED' || asset.status === 'DISPOSED'
        ? ASSET_STATUS_LABEL[asset.status]
        : null;
  const spaceLeft = cart.capacity ? cart.capacity - cart.deviceCount : null;
  const blocked = selected.some((asset) => problem(asset)) || (spaceLeft !== null && selected.length > spaceLeft);

  const mutation = useMutation({
    mutationFn: () => api.post(`/carts/${cart.id}/assets`, { assetIds: selected.map((asset) => asset.id) }),
    onSuccess: () => {
      invalidateInventory(queryClient);
      toast.success(`Put ${selected.length} device${selected.length === 1 ? '' : 's'} on ${cart.name}`);
      onClose();
    },
    onError: (err) => setError(errorMessage(err)),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Add devices to ${cart.name}`}
      description={`They’ll be kept in ${homeOf(cart)}${spaceLeft !== null ? ` · room for ${Math.max(spaceLeft, 0)} more` : ''}`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={mutation.isPending} disabled={selected.length === 0 || blocked} onClick={() => mutation.mutate()}>
            {selected.length ? `Add ${selected.length} device${selected.length === 1 ? '' : 's'}` : 'Add devices'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && <Alert tone="red">{error}</Alert>}
        <AssetSearch
          inline
          excludeIds={[...onCartIds, ...selected.map((asset) => asset.id)]}
          onSelect={(asset) => setSelected((current) => [...current, asset])}
          placeholder="Scan or search by asset tag, serial, or name"
        />
        {selected.length > 0 && (
          <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
            {selected.map((asset) => (
              <li key={asset.id} className="flex items-center gap-3 px-3 py-2 text-sm">
                <span className="w-24 shrink-0 font-mono text-xs font-semibold text-brand-800">{asset.assetTag}</span>
                <span className="min-w-0 flex-1 truncate">{asset.name}</span>
                {problem(asset) && <Badge tone="red">{problem(asset)}</Badge>}
                <button
                  type="button"
                  onClick={() => setSelected((current) => current.filter((item) => item.id !== asset.id))}
                  className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                  aria-label={`Remove ${asset.assetTag}`}
                >
                  <X className="size-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
        {spaceLeft !== null && selected.length > spaceLeft && <Alert tone="amber">{cart.name} only has room for {Math.max(spaceLeft, 0)} more.</Alert>}
      </div>
    </Modal>
  );
}
